import {
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Divider,
  CardActions,
  Grid,
} from '@mui/material'
import { Cancel as CancelIcon, QrCode2Outlined } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { Header, Text, ButtonCustom } from './styles'

const CONTAINER_LABELS = ['DAP', 'HT', 'Carbono Alm.']
const LIST_LABELS = ['Familia', 'Sitio']
const CHIP_LABELS = [
  'Enfermedades',
  'Problema físico',
  'Riesgos',
  'Plaga',
  'Recomendación',
]
const paddingInlineBody = 16

export default function PopupSkeleton({ showDescriptionBotton, onClose }) {
  const theme = useTheme()
  const fontFamilyType = theme.typography.fontFamily

  if (showDescriptionBotton) {
    return (
      <Box
        sx={{ minWidth: '18em', maxWidth: '24em', backgroundColor: 'background.paper' }}>
        <Header style={{ minHeight: '3.8em', paddingLeft: 4 }}>
          <Text
            sx={{
              textAlign: 'center',
              width: '100%',
              position: 'relative',
              zIndex: 1,
              px: 2,
            }}>
            <Skeleton
              variant="text"
              height={17}
              width="60%"
              sx={{ mx: 'auto', bgcolor: 'rgba(255,255,255,0.25)' }}
            />
            <Skeleton
              variant="text"
              height={15}
              width="40%"
              sx={{ mx: 'auto', bgcolor: 'rgba(255,255,255,0.2)', mt: 0.3 }}
            />
          </Text>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: '30px',
              px: 1,
              paddingRight: '10px',
              paddingBottom: '5px',
              marginRight: 0.5,
            }}>
            <CancelIcon sx={{ fill: '#a8f446', width: 20, height: 16 }} />
          </IconButton>
        </Header>
        <Box sx={{ px: 1.5, pt: 0.25, pb: 1 }}>
          <Skeleton variant="rounded" height={36} sx={{ mb: 0.5 }} />
          <Box sx={{ textAlign: 'center', pt: 0.25, pb: 0.25 }}>
            <Skeleton
              variant="text"
              width="35%"
              height={14}
              sx={{ mx: 'auto', mb: 0.5 }}
            />
            <Skeleton variant="text" width="70%" height={18} sx={{ mx: 'auto' }} />
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minWidth: '25em',
        maxWidth: 'none',
        boxShadow: 'none',
        backgroundColor: 'background.paper',
      }}>
      {/* Header: skeleton para nombre y nombre científico */}
      <Header style={{ minHeight: '3.8em', paddingLeft: 4 }}>
        <Text
          sx={{
            textAlign: 'center',
            width: '100%',
            position: 'relative',
            zIndex: 1,
            px: 2,
          }}>
          <Skeleton
            variant="text"
            height={18}
            width="60%"
            sx={{ mx: 'auto', bgcolor: 'rgba(255,255,255,0.25)' }}
          />
          <Skeleton
            variant="text"
            height={15}
            width="42%"
            sx={{ mx: 'auto', bgcolor: 'rgba(255,255,255,0.2)', mt: 0.3 }}
          />
        </Text>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            width: '30px',
            px: 1,
            paddingRight: '10px',
            paddingBottom: '5px',
            marginRight: 0.5,
          }}>
          <CancelIcon sx={{ fill: '#a8f446', width: 20, height: 16 }} />
        </IconButton>
      </Header>

      <div style={{ paddingInline: paddingInlineBody, paddingTop: 6 }}>
        {/* ContainerPopover: labels fijos DAP / HT / Carbono, valores en skeleton */}
        <Box sx={{ py: 0, paddingBottom: '8px' }}>
          <Box sx={{ paddingTop: '8px' }}>
            <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="8px">
              {CONTAINER_LABELS.map(label => (
                <Box key={label} sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      fontFamily: fontFamilyType,
                      fontWeight: 600,
                      fontSize: '12px',
                      lineHeight: '14px',
                      textAlign: 'center',
                      paddingBottom: '4px',
                      color: 'primary.main',
                      letterSpacing: '0.02em',
                    }}>
                    {label}
                  </Box>
                  <Skeleton variant="rounded" height={26} sx={{ borderRadius: '8px' }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Lista: labels fijos Familia / Sitio, valores en skeleton */}
        <List sx={{ py: 0 }}>
          {LIST_LABELS.map(label => (
            <ListItem
              key={label}
              component="div"
              disablePadding
              sx={{ minHeight: 18, justifyContent: 'initial', py: 0 }}>
              <ListItemText sx={{ marginTop: '2px', px: 1 }}>
                <Grid
                  container
                  sx={{
                    fontFamily: fontFamilyType,
                    fontSize: '13px',
                    lineHeight: '12px',
                    textAlign: 'center',
                    color: theme.palette.text.primary,
                  }}>
                  <Grid item xs={3} alignContent="center" fontWeight="600">
                    {label}:
                  </Grid>
                  <Grid item xs={9} alignContent="center">
                    <Skeleton
                      variant="rounded"
                      height={22}
                      sx={{
                        borderRadius: '10px',
                        border: '2px solid rgba(0, 0, 0, 0.04)',
                      }}
                    />
                  </Grid>
                </Grid>
              </ListItemText>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ mt: 0.2, bgcolor: 'primary.main' }} />

        {/* Chips: labels fijos, badge en skeleton circular */}
        <Box
          sx={{
            display: 'flex',
            flexFlow: 'row wrap',
            justifyContent: 'space-evenly',
            gap: '8px',
            pt: 1,
          }}>
          {CHIP_LABELS.map(label => (
            <Box
              key={label}
              sx={{
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)' },
              }}>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Chip
                  label={label}
                  size="small"
                  sx={{
                    fontFamily: fontFamilyType,
                    fontSize: '0.985em',
                    height: '18px',
                    fontWeight: 600,
                    backgroundColor: 'rgb(25 118 210 / 9%)',
                    color: '#000',
                  }}
                />
                <Skeleton
                  variant="circular"
                  width={14}
                  height={14}
                  sx={{ position: 'absolute', top: -5, right: -5 }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Footer: QR icon + skeleton código, Ver más (desactivado), placeholder derecho */}
        <CardActions
          sx={{
            display: 'flex',
            alignItems: 'center',
            pt: 1,
            px: 0,
            m: 0,
            '& > *': { flex: 1, display: 'flex', alignItems: 'center' },
            '& > :not(:first-of-type)': { marginLeft: 0 },
          }}>
          <Box sx={{ justifyContent: 'flex-start' }}>
            <QrCode2Outlined sx={{ fontSize: 26, color: 'primary.main', opacity: 0.4 }} />
            <Skeleton
              variant="rounded"
              width={38}
              height={22}
              sx={{ ml: 0.5, borderRadius: '4px' }}
            />
          </Box>
          <Box sx={{ justifyContent: 'center' }}>
            <ButtonCustom
              variant="contained"
              size="small"
              disabled
              sx={{
                height: 27,
                width: 84,
                fontFamily: fontFamilyType,
                fontSize: 14,
                fontWeight: 550,
              }}>
              Ver más
            </ButtonCustom>
          </Box>
          <Box sx={{ justifyContent: 'space-around' }}>
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
        </CardActions>
      </div>
    </Box>
  )
}
